/**
 * Chats the server has refused to save from this tab.
 *
 * Studio only lets a chat's owner write it (a foreign save used to overwrite
 * the row — 30 times on 2026-09-19). A tab still open on a chat that was
 * handed off via a claim link, or opened by someone who doesn't own it, now
 * gets 403 on every save. The client kept retrying the post-stream save and
 * re-posting the 30-second autosave, so one stale tab produced a steady stream
 * of 403s and a scary "couldn't save" toast. A 403 won't change on retry:
 * remember the chat and stop saving it for the rest of this page's life.
 *
 * 401 is not handled here — the axios interceptor refreshes and retries it.
 */
import { isAxiosError } from 'axios';

const refused = new Set<string>();

/** The save was refused for ownership/permission, not a transient failure. */
export function isSaveForbidden(err: unknown): boolean {
  return isAxiosError(err) && err.response?.status === 403;
}

/** Whether saves for this chat were already refused in this tab. */
export function isChatSaveRefused(chatId: string): boolean {
  return refused.has(chatId);
}

/**
 * Remember that saves for this chat are refused. Returns true only the first
 * time, so the caller tells the user once rather than on every attempt.
 */
export function markChatSaveRefused(chatId: string): boolean {
  if (refused.has(chatId)) {
    return false;
  }

  refused.add(chatId);

  return true;
}

/** Test hook. */
export function resetRefusedChatSaves(): void {
  refused.clear();
}
